import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout.jsx';
import { api } from '../../api/client.js';
import { Swal2 } from '../../lib/swal.js';
import { MORPH_BTN, MORPH_BTN_SM } from '../../lib/ui.js';

const BULAN = ['', 'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

function fmtTanggal(ymd) {
  if (!ymd) return 'Belum ditentukan';
  const [y, m, d] = ymd.split('-');
  return `${parseInt(d, 10)} ${BULAN[parseInt(m, 10)]} ${y}`;
}

function fmtTanggalPendek(ymd) {
  if (!ymd) return '-';
  const [y, m, d] = ymd.split('-');
  return `${parseInt(d, 10)} ${BULAN[parseInt(m, 10)].slice(0, 3)} ${y}`;
}

function fmtWaktu(dt) {
  const d = new Date(dt);
  const tgl = d.toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' });
  const jam = d.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
  return `${tgl}, ${jam}`;
}

// Bold-kan bagian teks yang cocok dengan kata pencarian
function highlightMatch(text, query) {
  if (!text) return text;
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <b className="font-bold text-neutral-900 dark:text-white">{text.slice(idx, idx + query.length)}</b>
      {text.slice(idx + query.length)}
    </>
  );
}

// Template balasan — port persis dari admin/kontak/index.php (JS template string)
function buildTemplate(k) {
  const tanggalAcara = fmtTanggal(k.tanggal_acara);
  const jamAcara = k.jam_acara ? k.jam_acara.slice(0, 5) : 'Belum ditentukan';
  const paketLayanan = k.paket_layanan || '-';
  const pesan = k.pesan || '-';
  const telepon = k.telepon || '-';

  return `Kepada Yth. ${k.nama},

Terima kasih atas pesan yang telah Bapak/Ibu sampaikan melalui website kami.

Kami telah menerima detail kebutuhan Bapak/Ibu sebagai berikut:

Rencana Acara    : ${tanggalAcara} ${jamAcara !== 'Belum ditentukan' ? 'Pukul ' + jamAcara : ''}
Paket Layanan    : ${paketLayanan}
Kebutuhan        : ${pesan}

Menindaklanjuti hal tersebut, kami dengan senang hati akan membantu mewujudkan acara Bapak/Ibu.

Tim kami akan segera menghubungi Bapak/Ibu melalui WhatsApp di nomor ${telepon} untuk membahas lebih lanjut mengenai konsep, paket yang tersedia, dan penawaran terbaik sesuai kebutuhan yang diharapkan.

Sementara itu, Bapak/Ibu juga dapat melihat portofolio dan layanan kami di website resmi kami.

Terima kasih,
Tim Layanan Pelanggan`;
}

export default function KontakList() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [unread, setUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const searchBoxRef = useRef(null);

  const [selected, setSelected] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyForm, setReplyForm] = useState({ subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = () => {
    setItems(null);
    api.get('/admin/kontak', { params: { q: search, page } }).then(({ data }) => {
      setItems(data.items);
      setTotal(data.total);
      setTotalPages(data.totalPages);
      setUnread(data.unread);
    });
  };
  useEffect(() => { load(); }, [page, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autocomplete: debounce ketik, ambil saran dari database (endpoint pencarian yang sama)
  useEffect(() => {
    const q = searchInput.trim();
    if (!q) { setSuggestions([]); setSuggestLoading(false); return; }
    setSuggestLoading(true);
    const t = setTimeout(() => {
      api.get('/admin/kontak', { params: { q, page: 1 } })
        .then(({ data }) => { setSuggestions((data.items || []).slice(0, 6)); setActiveIdx(-1); })
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Tutup dropdown saat klik di luar
  useEffect(() => {
    const onClickOutside = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowSuggest(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const pickSuggestion = (item) => {
    setSearchInput(item.nama);
    setSearch(item.nama);
    setPage(1);
    setShowSuggest(false);
    open(item.id);
  };

  const onSearchKeyDown = (e) => {
    if (!showSuggest || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      pickSuggestion(suggestions[activeIdx]);
    } else if (e.key === 'Escape') {
      setShowSuggest(false);
    }
  };

  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
    setShowSuggest(false);
  };

  const open = async (id) => {
    const { data } = await api.get(`/admin/kontak/${id}`);
    setSelected(data.item);
    setSelectedBooking(data.booking);
    load();
  };

  const close = () => { setSelected(null); setSelectedBooking(null); };

  const markAllRead = async () => {
    const confirmed = await Swal2.confirm(
      'Tandai Semua Dibaca?',
      'Semua pesan akan ditandai sebagai sudah dibaca.',
      'Ya, Tandai!',
      'question'
    );
    if (!confirmed) return;
    const { data } = await api.post('/admin/kontak/mark-all-read');
    Swal2.toast('success', data.message);
    load();
  };

  const confirmBooking = async () => {
    const confirmed = await Swal2.confirm(
      'Konfirmasi ke Jadwal?',
      'Booking dari pesan ini akan dikonfirmasi dan ditampilkan di jadwal publik beranda.',
      'Ya, Konfirmasi!',
      'success'
    );
    if (!confirmed) return;
    setConfirming(true);
    try {
      const { data } = await api.post(`/admin/kontak/${selected.id}/confirm-booking`);
      Swal2.toast('success', data.message);
      const { data: fresh } = await api.get(`/admin/kontak/${selected.id}`);
      setSelectedBooking(fresh.booking);
    } catch (err) {
      Swal2.toast('error', err.response?.data?.message || 'Gagal mengonfirmasi booking.');
    } finally {
      setConfirming(false);
    }
  };

  const openReply = () => {
    setReplyForm({
      subject: `Re: Permintaan Informasi Acara - ${selected.nama}`,
      message: buildTemplate(selected),
    });
    setReplyOpen(true);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/admin/kontak/${selected.id}/reply`, replyForm);
      setReplyOpen(false);
      Swal2.toast('success', data.message);
    } catch (err) {
      Swal2.toast('error', err.response?.data?.message || 'Gagal mengirim email.');
    } finally {
      setSending(false);
    }
  };

  const remove = async (id, nama) => {
    const confirmed = await Swal2.confirmDelete(`${nama} (pesan dan booking terkait)`);
    if (!confirmed) return;
    await api.delete(`/admin/kontak/${id}`);
    if (selected?.id === id) close();
    Swal2.toast('success', 'Pesan dan booking terkait berhasil dihapus.');
    load();
  };

  const inputCls = 'w-full px-4 py-3 rounded-xl border text-sm outline-none bg-neutral-50 dark:bg-neutral-800 dark:text-white border-neutral-200 dark:border-neutral-700 focus:border-forest-500 focus:ring-2 focus:ring-forest-500/20';
  const labelCls = 'block text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1.5';
  const bookingConfirmed = selectedBooking && selectedBooking.status === 'confirmed' && selectedBooking.is_public;

  return (
    <AdminLayout title="Pesan Masuk">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
            Pesan Masuk
            {unread > 0 && (
              <span className="text-xs px-2.5 py-0.5 rounded-full text-white font-bold animate-pulse" style={{ background: '#DC503C' }}>{unread} Baru</span>
            )}
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">Kelola data pesan dan kebutuhan konsultasi klien</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full md:w-auto">
          <form onSubmit={submitSearch} className="flex items-center gap-2 flex-1 sm:flex-none">
            <div ref={searchBoxRef} className="relative flex-1 sm:flex-none">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-base">search</span>
              <input
                value={searchInput}
                onChange={(e) => { setSearchInput(e.target.value); setShowSuggest(true); }}
                onFocus={() => { if (searchInput.trim()) setShowSuggest(true); }}
                onKeyDown={onSearchKeyDown}
                placeholder="Cari nama atau email..."
                autoComplete="off"
                className="w-full sm:w-56 pl-9 pr-4 py-2.5 rounded-xl border text-xs outline-none bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white border-neutral-200 dark:border-neutral-700 focus:border-forest-500 focus:ring-2 focus:ring-forest-500/20 transition-all"
              />

              {showSuggest && searchInput.trim() && (
                <div className="absolute z-30 top-[calc(100%+6px)] left-0 w-full sm:w-80 rounded-xl border bg-white dark:bg-neutral-800 shadow-xl overflow-hidden" style={{ borderColor: 'color-mix(in oklab, var(--accent) 20%, transparent)' }}>
                  {suggestLoading ? (
                    <div className="px-4 py-3 text-xs text-neutral-400">Mencari...</div>
                  ) : suggestions.length === 0 ? (
                    <div className="px-4 py-3 text-xs text-neutral-400">Tidak ada hasil untuk "{searchInput}"</div>
                  ) : (
                    suggestions.map((item, i) => (
                      <button
                        type="button"
                        key={item.id}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => pickSuggestion(item)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${i === activeIdx ? 'bg-neutral-100 dark:bg-neutral-700' : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'}`}
                      >
                        <span className="material-symbols-outlined text-neutral-400 text-base flex-shrink-0">search</span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-xs font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                            {highlightMatch(item.nama, searchInput)}
                          </span>
                          {item.email && (
                            <span className="block text-[11px] text-neutral-400 truncate">{highlightMatch(item.email, searchInput)}</span>
                          )}
                        </span>
                        {!item.is_read && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
            <button type="submit" className={`px-4 py-2.5 rounded-lg text-xs font-bold text-white ${MORPH_BTN_SM}`} style={{ background: 'var(--accent)' }}>
              Cari
            </button>
          </form>
          {unread > 0 && (
            <button
              onClick={markAllRead}
              className={`flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold border bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-700 ${MORPH_BTN_SM}`}
              style={{ borderColor: 'color-mix(in oklab, var(--accent) 30%, transparent)', color: 'var(--accent)' }}
            >
              <span className="material-symbols-outlined text-base">done_all</span> Semua Dibaca
            </button>
          )}
        </div>
      </div>

      {/* Tabel */}
      <div className="rounded-2xl border bg-white dark:bg-neutral-900 shadow-sm overflow-hidden" style={{ borderColor: 'color-mix(in oklab, var(--accent) 15%, transparent)' }}>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="text-left text-[11px] font-bold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider bg-neutral-50/70 dark:bg-neutral-800/40 border-b border-neutral-100 dark:border-neutral-800">
                <th className="py-3.5 px-5">Pengirim</th>
                <th className="py-3.5 px-4 hidden md:table-cell">Kontak Korespondensi</th>
                <th className="py-3.5 px-4 hidden sm:table-cell">Paket Layanan</th>
                <th className="py-3.5 px-4">Tanggal</th>
                <th className="py-3.5 px-4">Status</th>
                <th className="py-3.5 px-5 text-center">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {items === null ? (
                <tr><td colSpan={6} className="text-center py-16 text-neutral-400">Memuat...</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-16 text-neutral-400 dark:text-neutral-500">
                    <span className="material-symbols-outlined text-5xl mb-3 block opacity-60">inbox</span>
                    <p className="text-sm font-medium">Tidak ditemukan data pesan masuk.</p>
                  </td>
                </tr>
              ) : items.map((item) => (
                <tr key={item.id} className={`hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors ${!item.is_read ? 'font-semibold' : ''}`}>
                  <td className="py-4 px-5">
                    <div className="flex items-center gap-2.5">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${!item.is_read ? 'bg-amber-500 ring-4 ring-amber-500/10' : 'bg-transparent'}`} />
                      <span className="text-sm text-neutral-900 dark:text-white">{item.nama}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 hidden md:table-cell text-xs text-neutral-500 dark:text-neutral-400 space-y-0.5">
                    <div className="text-neutral-800 dark:text-neutral-300 font-medium">{item.email || <span className="text-neutral-400">-</span>}</div>
                    <div className="text-neutral-400 text-[11px]">{item.telepon || '-'}</div>
                  </td>
                  <td className="py-4 px-4 hidden sm:table-cell text-xs">
                    {item.paket_layanan
                      ? <span className="px-2 py-0.5 rounded-md text-[11px] font-bold bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300">{item.paket_layanan}</span>
                      : <span className="text-neutral-400">-</span>}
                  </td>
                  <td className="py-4 px-4 text-xs text-neutral-500 dark:text-neutral-400">{fmtTanggalPendek(item.created_at.slice(0, 10))}</td>
                  <td className="py-4 px-4">
                    <span
                      className="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider"
                      style={item.is_read ? { background: 'color-mix(in oklab, var(--accent) 10%, transparent)', color: 'var(--accent)' } : { background: 'rgba(220,80,60,0.1)', color: '#DC503C' }}
                    >
                      {item.is_read ? 'Dibaca' : 'Baru'}
                    </span>
                  </td>
                  <td className="py-4 px-5">
                    <div className="flex items-center justify-center gap-2">
                      <button onClick={() => open(item.id)} title="Lihat Detail" className="group w-9 h-9 rounded-lg flex items-center justify-center text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 hover:bg-forest-500 hover:text-white hover:rounded-xl active:rounded-3xl active:scale-95 transition-all duration-300">
                        <span className="material-symbols-outlined text-sm">visibility</span>
                      </button>
                      <button onClick={() => remove(item.id, item.nama)} title="Hapus" className="group w-9 h-9 rounded-lg flex items-center justify-center text-red-400 bg-red-50 dark:bg-red-950/20 hover:bg-red-500 hover:text-white hover:rounded-xl active:rounded-3xl active:scale-95 transition-all duration-300">
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-800/20">
            <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Halaman {page} dari {totalPages} (Total: {total} pesan)</span>
            <div className="flex gap-1.5">
              {Array.from({ length: totalPages }).map((_, i) => {
                const p = i + 1;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold transition-all duration-300 hover:rounded-lg active:scale-90 ${p === page ? 'text-white shadow-sm hover:shadow-md' : 'border border-neutral-200 dark:border-neutral-700 text-forest-500 hover:bg-neutral-50 dark:hover:bg-neutral-700'}`}
                    style={p === page ? { background: 'var(--accent)' } : undefined}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Modal Detail Pesan */}
      {selected && !replyOpen && (
        <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={close}>
          <div onClick={(e) => e.stopPropagation()} className="max-w-lg w-full bg-white dark:bg-neutral-800 rounded-3xl p-6 sm:p-8 shadow-2xl relative border border-neutral-100 dark:border-neutral-700 max-h-[90vh] overflow-y-auto">
            <button onClick={close} className="group absolute top-5 right-5 w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-md hover:rounded-xl active:rounded-3xl active:scale-95 transition-all duration-300" style={{ background: 'var(--accent)' }}>
              <span className="material-symbols-outlined text-sm transition-transform duration-300 group-hover:rotate-90">close</span>
            </button>
            <h3 className="font-display text-xl font-bold text-neutral-900 dark:text-white mb-5 flex items-center gap-2">
              <span className="material-symbols-outlined text-forest-500">drafts</span>
              Detail Pesan Masuk
            </h3>

            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4 bg-neutral-50 dark:bg-neutral-900/50 p-4 rounded-2xl border border-neutral-100 dark:border-neutral-800">
                <div>
                  <span className="text-[11px] text-neutral-400 font-bold uppercase tracking-wider block mb-0.5">Nama Pengirim</span>
                  <span className="font-semibold text-neutral-900 dark:text-white">{selected.nama}</span>
                </div>
                <div>
                  <span className="text-[11px] text-neutral-400 font-bold uppercase tracking-wider block mb-0.5">Waktu Kirim</span>
                  <span className="text-neutral-700 dark:text-neutral-300 font-medium">{fmtWaktu(selected.created_at)}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <span className="text-[11px] text-neutral-400 font-bold uppercase tracking-wider block mb-1">Email</span>
                  {selected.email ? (
                    <a href={`mailto:${selected.email}`} className="group text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1 hover:text-forest-500 transition-all duration-300">
                      <span className="material-symbols-outlined text-xs transition-transform duration-300 group-hover:-translate-y-0.5">mail</span>
                      {selected.email}
                    </a>
                  ) : <span className="text-neutral-400 dark:text-neutral-500">-</span>}
                </div>
                <div>
                  <span className="text-[11px] text-neutral-400 font-bold uppercase tracking-wider block mb-1">No. Telepon</span>
                  {selected.telepon ? (
                    <a href={`tel:${selected.telepon}`} className="group text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1 hover:text-forest-500 transition-all duration-300">
                      <span className="material-symbols-outlined text-xs transition-transform duration-300 group-hover:rotate-12">call</span>
                      {selected.telepon}
                    </a>
                  ) : <span className="text-neutral-400 dark:text-neutral-500">-</span>}
                </div>
              </div>

              {(selected.tanggal_acara || selected.paket_layanan) && (
                <div className="grid grid-cols-2 gap-4 border-t border-dashed border-neutral-200 dark:border-neutral-700 pt-3">
                  {selected.tanggal_acara && (
                    <div>
                      <span className="text-[11px] text-neutral-400 font-bold uppercase tracking-wider block mb-0.5">Rencana Acara</span>
                      <span className="text-neutral-700 dark:text-neutral-300 font-medium flex items-center flex-wrap gap-x-1.5">
                        <span className="material-symbols-outlined text-xs">calendar_month</span>
                        {fmtTanggalPendek(selected.tanggal_acara)}
                        {selected.jam_acara && (
                          <span className="text-neutral-500 dark:text-neutral-400 flex items-center gap-0.5">
                            <span className="material-symbols-outlined text-xs">schedule</span>
                            {selected.jam_acara.slice(0, 5)}
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {selected.paket_layanan && (
                    <div>
                      <span className="text-[11px] text-neutral-400 font-bold uppercase tracking-wider block mb-1">Paket Layanan</span>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-forest-500/10 text-forest-600 dark:text-forest-400">
                        {selected.paket_layanan}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selected.pesan && (
                <div className="pt-2">
                  <span className="text-[11px] text-neutral-400 font-bold uppercase tracking-wider block mb-1.5">Isi Pesan Kebutuhan</span>
                  <div className="p-4 rounded-xl text-neutral-700 dark:text-neutral-300 leading-relaxed border border-neutral-100 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900/30 max-h-40 overflow-y-auto whitespace-pre-line">
                    {selected.pesan}
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2.5 mt-6 pt-4 border-t border-neutral-100 dark:border-neutral-700">
              {selected.tanggal_acara && (
                bookingConfirmed ? (
                  <>
                    <span className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-bold text-emerald-700 bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/30">
                      <span className="material-symbols-outlined text-sm">event_available</span>
                      Sudah di Jadwal Publik
                    </span>
                    <Link to="/admin/booking" className="group flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-bold text-white shadow-sm hover:rounded-xl active:rounded-3xl active:scale-95 transition-all duration-300" style={{ background: 'var(--accent)' }}>
                      <span className="material-symbols-outlined text-sm">calendar_month</span>
                      Lihat Jadwal
                    </Link>
                  </>
                ) : (
                  <button disabled={confirming} onClick={confirmBooking} className="group flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-bold text-white bg-emerald-600 shadow-sm hover:bg-emerald-700 hover:rounded-xl active:rounded-3xl active:scale-95 disabled:opacity-60 transition-all duration-300">
                    <span className="material-symbols-outlined text-sm transition-transform duration-300 group-hover:scale-110">event_available</span>
                    {confirming ? 'Memproses...' : 'Konfirmasi ke Jadwal'}
                  </button>
                )
              )}
              {selected.email && (
                <button onClick={openReply} className="group flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-bold text-white shadow-sm hover:rounded-xl active:rounded-3xl active:scale-95 transition-all duration-300" style={{ background: 'var(--accent)' }}>
                  <span className="material-symbols-outlined text-base transition-transform duration-300 group-hover:-rotate-12">reply</span>
                  Balas Email
                </button>
              )}
              {selected.telepon && (
                <a href={`https://wa.me/${selected.telepon.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="group flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-xs font-bold text-white shadow-sm hover:rounded-xl active:rounded-3xl active:scale-95 transition-all duration-300" style={{ background: '#25D366' }}>
                  <span className="material-symbols-outlined text-base transition-transform duration-300 group-hover:scale-110">chat</span>
                  Chat WhatsApp
                </a>
              )}
              <button onClick={close} className="group px-4 py-2.5 rounded-lg text-xs font-bold border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 bg-white dark:bg-neutral-800 ml-auto hover:bg-neutral-50 dark:hover:bg-neutral-700 hover:rounded-xl active:rounded-3xl active:scale-95 transition-all duration-300">
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Balas Email */}
      {replyOpen && selected && (
        <div className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4" onClick={() => setReplyOpen(false)}>
          <form onSubmit={sendReply} onClick={(e) => e.stopPropagation()} className="max-w-2xl w-full bg-white dark:bg-neutral-800 rounded-3xl p-6 sm:p-8 shadow-2xl relative border border-neutral-100 dark:border-neutral-700 max-h-[90vh] overflow-y-auto space-y-4">
            <button type="button" onClick={() => setReplyOpen(false)} className="absolute top-5 right-5 w-8 h-8 rounded-lg flex items-center justify-center text-white hover:rounded-xl transition-all duration-300" style={{ background: 'var(--accent)' }}>
              <span className="material-symbols-outlined text-sm">close</span>
            </button>
            <h3 className="font-display text-xl font-bold text-neutral-900 dark:text-white mb-1 flex items-center gap-2">
              <span className="material-symbols-outlined text-forest-500">reply</span>
              Balas Email: {selected.nama}
            </h3>
            <div>
              <label className={labelCls}>Kepada</label>
              <input value={selected.email} readOnly className={`${inputCls} bg-neutral-100 dark:bg-neutral-900 text-neutral-500`} />
            </div>
            <div>
              <label className={labelCls}>Subjek</label>
              <input className={inputCls} value={replyForm.subject} onChange={(e) => setReplyForm((f) => ({ ...f, subject: e.target.value }))} />
            </div>
            <div>
              <label className={labelCls}>Template Pesan</label>
              <textarea rows={14} className={`${inputCls} resize-none font-mono text-sm`} value={replyForm.message} onChange={(e) => setReplyForm((f) => ({ ...f, message: e.target.value }))} />
            </div>
            <div className="flex gap-3 pt-2">
              <button disabled={sending} type="submit" className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white ${MORPH_BTN}`} style={{ background: 'var(--accent)' }}>
                <span className="material-symbols-outlined text-base">send</span>
                {sending ? 'Mengirim...' : 'Kirim Email'}
              </button>
              <button type="button" onClick={() => setReplyOpen(false)} className={`px-4 py-2.5 rounded-xl text-sm font-bold border border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 ${MORPH_BTN}`}>
                Batal
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}
